import { DatePipe } from '@angular/common';
import { HttpClient,  HttpRequest, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ItsmGenerator } from '../mock/itsm.generator';
import { Asset, Case, Contact, Milestone } from '../model/itsm';
import { UpdateCase, UpdateCaseFields } from '../model/persistCriteria';
import { SupportCaseSearchCriteria } from '../model/searchCriteria';
import { Utilities } from '../utilities';
import { Observable } from 'rxjs';
import { constants } from '../constants';

@Injectable()
export class ItsmService {
    private caseURL = environment.endpoints.itsm + 'case/';
    private caseCreateURL = environment.endpoints.itsm + 'case';
    private caseUpdateURL = environment.endpoints.itsm + 'case';
    private caseSearchURL = environment.endpoints.itsm + 'search';
    private secuityIncidentBenchmarking = environment.endpoints.itsm + 'secib';
    private caseSearchListURL = environment.endpoints.itsm + 'case/search';
    private assetsURL = environment.endpoints.itsm + 'assets';
    private assetURL = environment.endpoints.itsm + 'asset/';
    private cvURL = environment.endpoints.itsm + 'cv/';
    private caseCountURL = environment.endpoints.itsm + 'caseCount';
    private caseSearchCountURL = environment.endpoints.itsm + 'searchCount'
    private contactsURL = environment.endpoints.itsm + 'contacts';
    private deleteContactURL = environment.endpoints.itsm + 'deletecontact';
    private addContactURL = environment.endpoints.itsm + 'contact';     // For Adding Contact
    private editContactURL = environment.endpoints.itsm + 'editcontact';     // For Editing Contact
    private caseCommentURL = environment.endpoints.itsm + 'case/comment';
    private fileURL =  environment.endpoints.itsm + 'case/attachment/';
    private mileStoneURL = environment.endpoints.itsm + 'milestones';
    private liveReportURL = environment.endpoints.itsm + 'search';
    private livePDFReport = environment.endpoints.itsm + 'livePDFReport';
    private services = environment.endpoints.itsm + 'services';
    private assetUpdateURL = environment.endpoints.itsm + 'updateci';

    datepipe: DatePipe = new DatePipe('en-GB')

    constructor(
        private http: HttpClient,
        private itsmGen: ItsmGenerator) { }

    case(caseRef: string): Promise<any>{
        return this.http
            .get(this.caseURL + caseRef)
            .toPromise()
            .then(response => response as any)
            .catch(this.handleError);
    }

    caseComment(commentPostObj: {parentId, commentBody, user, silentUpdate}): Promise<any>{
        return this.http
            .post(this.caseCommentURL, JSON.stringify(commentPostObj))
            .toPromise()
            .then(response => response as Case)
            .catch(this.handleError);
    }

    caseCreate(caseObj: Case): Promise<Case>{
        return this.http
            .post(this.caseCreateURL, JSON.stringify(caseObj))
            .toPromise()
            .then(response => response as Case)
            .catch(this.handleError);
    }

    caseUpdate(caseObj: Case): Promise<Case>{

        let crit = new UpdateCase();
        crit.update = new UpdateCaseFields();
        crit.id = caseObj.id;
        crit.update.subject = caseObj.subject;
        crit.update.description = caseObj.description;
        crit.update.priority = caseObj.priority;
        crit.update.assetId = caseObj.assetId;
        crit.update.contactId = caseObj.contactId;
        crit.update.secondContactId = caseObj.secondContactId;

        return this.http
            .patch(this.caseUpdateURL, JSON.stringify(crit))
            .toPromise()
            .then(response => response as Case)
            .catch(this.handleError);
    }

    caseSearch(searchCriteria: SupportCaseSearchCriteria): Promise<Case[] | any>{
        let query = this.caseSearchURL + '?';
        if(searchCriteria.count){
            query = this.caseSearchCountURL + '?';
        }

        if(searchCriteria.caseNumber){
            query += 'caseNumber=' + encodeURIComponent(searchCriteria.caseNumber);
        }
        if(searchCriteria.export){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'export=true';
            query += '&reportName=' + searchCriteria.pdfName;
            query += '&pdfAccessLevel=' + searchCriteria.pdfAccessLevel;
        }
        if(searchCriteria.securityAnaltyics){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'securityAnalytics=true&FromCreatedDate=' + Utilities.get6MonthsAgo();
        }

        if(searchCriteria.securityAnalyt){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'securityAnalytics=true';
        }

        if(searchCriteria.createdFrom){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'fromCreatedDate=' + encodeURIComponent(searchCriteria.createdFrom);
        }
        if(searchCriteria.createdTo){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'toCreatedDate=' + encodeURIComponent(searchCriteria.createdTo);
        }
        if(searchCriteria.closedDateFrom){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'fromClosedDate=' + encodeURIComponent(searchCriteria.closedDateFrom);
        }
        if(searchCriteria.closedDateTo){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'toClosedDate=' + encodeURIComponent(searchCriteria.closedDateTo);
        }
        if(searchCriteria.updatedFrom){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'fromLastModifiedDate=' + encodeURIComponent(searchCriteria.updatedFrom);
        }
        if(searchCriteria.updatedTo){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'toLastModifiedDate=' + encodeURIComponent(searchCriteria.updatedTo);
        }
        if(searchCriteria.type){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'type=' + encodeURIComponent(searchCriteria.type);
        }
        if(searchCriteria.status){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'status=' + encodeURIComponent(searchCriteria.status);
        }
        if(searchCriteria.contact){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'contact=' + encodeURIComponent(searchCriteria.contact);
        }
        if(searchCriteria.contactEmail){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'contactmail=' + encodeURIComponent(searchCriteria.contactEmail);
        }
        if(searchCriteria.contactEmailUnassigned){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'contactemailunassigned=' + encodeURIComponent(searchCriteria.contactEmailUnassigned);
        }
        if(searchCriteria.createdByEmail){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'createdByEmail=' + encodeURIComponent(searchCriteria.createdByEmail);
        }
        if(searchCriteria.category){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'category=' + encodeURIComponent(searchCriteria.category);
        }
        if(searchCriteria.priority){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'priority=' + encodeURIComponent(searchCriteria.priority);
        }
        if(searchCriteria.subject){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'subject=' + encodeURIComponent(searchCriteria.subject);
        }
        if(searchCriteria.description){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'description=' + encodeURIComponent(searchCriteria.description);
        }
        if(searchCriteria.assetId){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'assetId=' + encodeURIComponent(searchCriteria.assetId);
        }
        if(searchCriteria.asset){
            if(!query.endsWith('?')){
                query += '&';
            }
            // remove possibly blank serialNumber/name from search values
            let value = searchCriteria.asset.split('||').filter(s => (s && s != "" && s != "n/a")).join('||')
            query += 'asset=' + encodeURIComponent(value);
        }
        if(searchCriteria.escalation){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'escalation=' + encodeURIComponent(searchCriteria.escalation);
        }
        if(searchCriteria.sort){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'sort=' + encodeURIComponent(searchCriteria.sort);
        }
        if(searchCriteria.sortBy){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'sortBy=' + encodeURIComponent(searchCriteria.sortBy);
        }
        
        if(searchCriteria.limit){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'limit=' + encodeURIComponent(searchCriteria.limit);
        }
        
        if(searchCriteria.skip){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'offset=' + encodeURIComponent(searchCriteria.skip);
        }

        if(searchCriteria.extended) {
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'extended=true';
        }

        if(searchCriteria.resolutionReport) {
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'resolutionReport=true';
        }

        return this.http
            .get(query)
            .toPromise()
            .then(response => response as Case[])
            .then(res => {
               var returnObj:any = {}
                if(searchCriteria.requestTracker.requestTracker){
                    returnObj = {
                        requestTracker:searchCriteria.requestTracker.count,
                        data: res
                    }
                }
                else {
                    returnObj = res;
                }
              
                return returnObj
            })
            .catch(this.handleError);
    }

    caseLoadList(searchCriteria: SupportCaseSearchCriteria): Promise<Case[] | any>{
        let caseIds = searchCriteria.caseRefs.join(",");
        return this.http
            .get(this.caseSearchURL + '?numbers='+caseIds)
            .toPromise()
            .then(response => response as Case[])
            .catch(this.handleError);
    }

    securityIncidentReport(date: string): Promise<any> {
        let query = this.caseSearchURL + '?' + 'status=Closed&escalation=To Customer,from Customer&securityAnalytics=true&FromCreatedDate=' + date;
        return this.http
            .get(query)
            .toPromise()
            .then(response => response as Case[])
            .catch(this.handleError);
    }


    /**
     * Filter all assets to a single asset
     * TODO may need a getAsset method
     * @param assetRef 
     */
    getAsset(assetRef: string): Promise<Asset>{
        return this.http
            .get(this.assetURL + assetRef)
            .toPromise()
            .then(response => response as Asset)
            .catch(this.handleError);
    }

    /**
     * Update asset
     * @param assetObject
     */
     updateAsset(assetObject: {}): Promise<void>{
        return this.http
            .post(this.assetUpdateURL, assetObject)
            .toPromise()
            .catch(this.handleError);
    }
    /**
     * Get assets from local storage if they are cached
     * else get them from the backend service
     */
    getCachedAssets(exportPDF:boolean=false, reportName:string = "", reportAccessLevel:string = ""): Promise<Asset[]>{
        return new Promise((resolve, reject) => {
            if(sessionStorage.getItem('itsmAssets') && !exportPDF){
                resolve(JSON.parse(sessionStorage.getItem('itsmAssets')));
            }else{
                this.getAssets(exportPDF, reportName, reportAccessLevel)
                    .then(res => {
                        let assets = res;

                        // improve names and sort assets
                        assets.forEach(a => {
                          a.fullName = a.name;
                          if (a.model && (a.model != "") && (a.model != a.name)) {
                            a.fullName = a.model + " | " + a.fullName;
                          }
                          if (a.serialNumber && (a.serialNumber != "")) {
                            a.fullName = a.fullName + " | " + a.serialNumber;
                          }
                          else {
                            a.fullName = a.fullName + " | n/a";
                          }
                        });
                        assets = assets.sort((a, b) => (a.fullName.toLowerCase() > b.fullName.toLowerCase()) ? 1 : -1);

                        // add details of source for asset dashboard
                        assets.forEach(a => {
                            a.source = 'Support';
                        })

                        sessionStorage.setItem('itsmAssets', JSON.stringify(assets));
                        resolve(assets)
                    })
                    .catch(err => {
                        reject(err);
                    });
            }
        });
    }


    /**
     * Get all assets from backend service
     */
    private getAssets(exportPDF: boolean, reportName:string, reportAccessLevel:string): Promise<Asset[]>{

        if(exportPDF){
            return this.http
            .get(this.assetsURL+ '?export=' + exportPDF + '&reportName=' + reportName + '&pdfAccessLevel=' + reportAccessLevel)
            .toPromise()
            .then(response => response as Asset[])
            .catch(this.handleError);
        }
        else{
            return this.http
            .get(this.assetsURL)
            .toPromise()
            .then(response => response as Asset[])
            .catch(this.handleError);
        }
      
    }

    /**
     * Get contacts from local storage if they are cached
     * else get them from the backend service
     */
    getCachedContacts(): Promise<Contact[]>{
        return new Promise((resolve, reject) => {
            if(sessionStorage.getItem('itsmContacts')){
                resolve(JSON.parse(sessionStorage.getItem('itsmContacts')));
            }else{
                this.getContacts()
                    .then(res => {
                        let contacts = res.sort((a, b) => (a.fullName.toLowerCase() > b.fullName.toLowerCase()) ? 1 : -1);

                        sessionStorage.setItem('itsmContacts', JSON.stringify(contacts));
                        resolve(res);
                    })
                    .catch(err => {
                        reject(err);
                    });
            }
        });
    }

    /**
     * Get all contacts from backend service
     */
    private getContacts(): Promise<Contact[]>{
        return this.http
            .get(this.contactsURL)
            .toPromise()
            .then(response => {
                return response as Contact[]
            })
            .catch(this.handleError);
    }
    /**
     * Handling Contacts 
     */
    deleteContact(id:string): Promise<any>{
        return this.http
            .get(this.deleteContactURL + '/' + id)
            .toPromise()
            .then((res:any) => res as JSON)
            .catch(this.handleError);
    }
    addContact(object:{}): Promise<any>{
        return this.http
            .post(this.addContactURL, object)
            .toPromise()
            .then((res:any) => res as JSON)
            .catch(this.handleError);
    }
    editContact(object:{}): Promise<any>{
        return this.http
            .post(this.editContactURL, object)
            .toPromise()
            .then((res:any) => res as JSON)
            .catch(this.handleError);
    }
    /**
     * Get milestones from local storage if they are cached
     * else get them from the backend service
     */
     getCachedMilestones(): Promise<Milestone[]>{
        return new Promise((resolve, reject) => {
            if(sessionStorage.getItem('milestones')){
                resolve(JSON.parse(sessionStorage.getItem('milestones')));
            }else{
                this.getMilestones()
                    .then(res => {
                        let milestones = res;
                        
                        sessionStorage.setItem('milestones', JSON.stringify(milestones));
                        resolve(milestones)
                    })
                    .catch(err => {
                        reject(err);
                    });
            }
        });
     }

    /**
    * Get all assets from backend service
    */
    private getMilestones(): Promise<Milestone[]>{
        return this.http
            .get(this.mileStoneURL)
            .toPromise()
            .then(response => response as Milestone[])
            .catch(this.handleError);
    }

    /**
     * Gets CV objects from session storage if they exist, if not
     * then get them from webservice
     * @param object 
     * @param type 
     */
    getCachedCV(object: string, type: string): Promise<string[]>{
        return new Promise((resolve, reject) => {
            if(sessionStorage.getItem('cvItem'+object+type)){
                resolve(JSON.parse(sessionStorage.getItem('cvItem'+object+type)));
            }else{
                this.getCV(object, type)
                    .then(res => {
                        sessionStorage.setItem('cvItem'+object+type, JSON.stringify(res));
                        resolve(res);
                    })
                    .catch(err => {
                        reject(err);
                    });
            }
        })
    }

    /**
     * Get CV object from backend service
     * @param object 
     * @param type 
     */
    private getCV(object: string, type: string): Promise<any[]>{
        return this.http
            .get(this.cvURL + object + '/' + type)
            .toPromise()
            .then(response => response as any[])
            .catch(this.handleError);
    }

    getCachedServices(): Promise<any[]>{
        return new Promise((resolve, reject) => {
            if(sessionStorage.getItem('itsmServices') && sessionStorage.getItem('itsmServices2')){
                let arr1 = JSON.parse(sessionStorage.getItem('itsmServices'))
                let arr2 = JSON.parse(sessionStorage.getItem('itsmServices2'))
                resolve(arr1.concat(arr2));
            }
            else if(sessionStorage.getItem('itsmServices')){
                resolve(JSON.parse(sessionStorage.getItem('itsmServices')))
            }
            else{
                this.getServices()
                    .then(res => {
                        try{
                            sessionStorage.setItem('itsmServices', JSON.stringify(res));
                        }
                        catch(ex){
                            console.log("exception for large data =>", ex)
                            
                            if(ex.code == 22){
                                let arr1 = res.splice(0,Math.floor(res.length/2))
                                let arr2 = res.splice(Math.floor(res.length/2), res.length)
                                sessionStorage.setItem('itsmServices', JSON.stringify(arr1));
                                sessionStorage.setItem('itsmServices2', JSON.stringify(arr2));
                            }
                        }
                        finally{
                            resolve(res);
                        }
                        
                       
                    })
                    .catch(err => {
                        reject(err);
                    });
            }
        })
    }

    /**
     * Get entitlement services from backend service
     */
     private getServices(): Promise<any[]>{
        return this.http
            .get(this.services)
            .toPromise()
            .then(response => response as any[])
            .catch(this.handleError);
    }

    cacheCasesCountUpdatedSinceLastLogin(date: Date): Promise<number> {
        return new Promise((resolve,reject) => {
            if(sessionStorage.getItem("itsmCasesUpdatedLastLoginCount")){
                resolve(Number(sessionStorage.getItem("itsmCasesUpdatedLastLoginCount")))
            }else{
                let tempSearchCriteria = new SupportCaseSearchCriteria();
                tempSearchCriteria.updatedFrom = Utilities.getDateAsString(date);
                tempSearchCriteria.updatedTo = Utilities.getTodayPlusDays(1);
                tempSearchCriteria.count = true;
                this.caseSearch(tempSearchCriteria)
                    .then(res => {
                        sessionStorage.setItem("itsmCasesUpdatedLastLoginCount", String(res));
                        resolve(res);
                    })
                    .catch(err => {
                        reject(err);
                    })
            }
        });
    }

    cacheCasesCountCreatedSinceLastLogin(date: Date): Promise<number> {
        return new Promise((resolve,reject) => {
            if(sessionStorage.getItem("itsmCasesCreatedLastLoginCount")){
                resolve(Number(sessionStorage.getItem("itsmCasesCreatedLastLoginCount")))
            }else{
                let tempSearchCriteria = new SupportCaseSearchCriteria();
                tempSearchCriteria.createdFrom = Utilities.getDateAsString(date);
                tempSearchCriteria.createdTo = Utilities.getTodayPlusDays(1);
                tempSearchCriteria.count = true;
                this.caseSearch(tempSearchCriteria)
                    .then(res => {
                        sessionStorage.setItem("itsmCasesCreatedLastLoginCount", String(res));
                        resolve(res);
                    })
                    .catch(err => {
                        reject(err);
                    })
            }
        });
    }

    upload(file: File, caseId: string): Promise<any> {
        let formData = new FormData();
        formData.append('file', file);
        
        return this.http
            .post(this.fileURL + caseId,  formData, {
                reportProgress: true,
                responseType: 'json'})
            .toPromise()
            .then(response => response as any)
            .catch(this.handleError);
    
    }

    getAttachment(id: string, caseId: string): Promise<any> {
        var headers = new HttpHeaders();
        headers = headers.set('Content-Encoding', 'gzip');
    
        return this.http.get(environment.endpoints.itsm + 'attachment/' + caseId + '/' + id, {headers: headers, responseType: 'blob'})
          .toPromise()
          .then(response => response as any)
          .catch(this.handleError);
    }
    getLiveReportSecIb(searchCriteria: SupportCaseSearchCriteria): Promise<Case[] | any>{
        let query = this.secuityIncidentBenchmarking + '?';
        if(searchCriteria.count){
            query = this.caseSearchCountURL + '?';
        }

        

        if(searchCriteria.caseNumber){
            query += 'caseNumber=' + encodeURIComponent(searchCriteria.caseNumber);
        }
        if(searchCriteria.createdFrom){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'fromCreatedDate=' + encodeURIComponent(searchCriteria.createdFrom);
        }
        if(searchCriteria.createdTo){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'toCreatedDate=' + encodeURIComponent(searchCriteria.createdTo);
        }
        if(searchCriteria.action){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'action=true';
       }
       if(searchCriteria.actor){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'actor=true';
       }
       if(searchCriteria.attributes){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'attributes=true';
       }
       if(searchCriteria.who){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'who=true';
       }
       if(searchCriteria.what){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'what=true';
       }
       if(searchCriteria.why){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'why=true';
       }
        if(searchCriteria.closedDateFrom){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'fromClosedDate=' + encodeURIComponent(searchCriteria.closedDateFrom);
        }
        if(searchCriteria.closedDateTo){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'toClosedDate=' + encodeURIComponent(searchCriteria.closedDateTo);
        }
        if(searchCriteria.updatedFrom){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'fromLastModifiedDate=' + encodeURIComponent(searchCriteria.updatedFrom);
        }
        if(searchCriteria.updatedTo){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'toLastModifiedDate=' + encodeURIComponent(searchCriteria.updatedTo);
        }
        if(searchCriteria.type){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'type=' + encodeURIComponent(searchCriteria.type);
        }
        if(searchCriteria.status){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'status=' + encodeURIComponent(searchCriteria.status);
        }
        if(searchCriteria.contact){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'contact=' + encodeURIComponent(searchCriteria.contact);
        }
        if(searchCriteria.contactEmail){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'contactmail=' + encodeURIComponent(searchCriteria.contactEmail);
        }
        if(searchCriteria.category){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'category=' + encodeURIComponent(searchCriteria.category);
        }
        if(searchCriteria.priority){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'priority=' + encodeURIComponent(searchCriteria.priority);
        }
        if(searchCriteria.subject){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'subject=' + encodeURIComponent(searchCriteria.subject);
        }
        if(searchCriteria.description){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'description=' + encodeURIComponent(searchCriteria.description);
        }
        if(searchCriteria.assetId){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'assetId=' + encodeURIComponent(searchCriteria.assetId);
        }
        if(searchCriteria.asset){
            if(!query.endsWith('?')){
                query += '&';
            }
            // remove possibly blank serialNumber/name from search values
            let value = searchCriteria.asset.split('||').filter(s => (s && s != "" && s != "n/a")).join('||')
            query += 'asset=' + encodeURIComponent(value);
        }
        if(searchCriteria.escalation){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'escalation=' + encodeURIComponent(searchCriteria.escalation);
        }
        if(searchCriteria.sort){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'sort=' + encodeURIComponent(searchCriteria.sort);
        }
        if(searchCriteria.sortBy){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'sortBy=' + encodeURIComponent(searchCriteria.sortBy);
        }
        
        if(searchCriteria.limit){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'limit=' + encodeURIComponent(searchCriteria.limit);
        }
        
        if(searchCriteria.skip){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'offset=' + encodeURIComponent(searchCriteria.skip);
        }
        if (searchCriteria.securityAnaltyics){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'SecurityAnalytics=true';
        }

        return this.http
            .get(query)
            .toPromise()
            .then(response => response as Case[])
            .then(res => {
               var returnObj:any = {}
                if(searchCriteria.requestTracker.requestTracker){
                    returnObj = {
                        requestTracker:searchCriteria.requestTracker.count,
                        data: res
                    }
                }
                else {
                    returnObj = res;
                }
              
                return returnObj
            })
            .catch(this.handleError);
    }
    getLiveReport(searchCriteria: SupportCaseSearchCriteria): Promise<Case[] | any>{
        let query = this.caseSearchURL + '?';
        if(searchCriteria.count){
            query = this.caseSearchCountURL + '?';
        }

        

        if(searchCriteria.caseNumber){
            query += 'caseNumber=' + encodeURIComponent(searchCriteria.caseNumber);
        }
        if(searchCriteria.createdFrom){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'fromCreatedDate=' + encodeURIComponent(searchCriteria.createdFrom);
        }
        if(searchCriteria.createdTo){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'toCreatedDate=' + encodeURIComponent(searchCriteria.createdTo);
        }
        if(searchCriteria.action){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'action=true';
       }
       if(searchCriteria.actor){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'actor=true';
       }
       if(searchCriteria.attributes){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'attributes=true';
       }
       if(searchCriteria.who){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'who=true';
       }
       if(searchCriteria.what){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'what=true';
       }
       if(searchCriteria.why){
            if(!query.endsWith('?')){
               query += '&';
           }
           query += 'why=true';
       }
        if(searchCriteria.closedDateFrom){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'fromClosedDate=' + encodeURIComponent(searchCriteria.closedDateFrom);
        }
        if(searchCriteria.closedDateTo){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'toClosedDate=' + encodeURIComponent(searchCriteria.closedDateTo);
        }
        if(searchCriteria.updatedFrom){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'fromLastModifiedDate=' + encodeURIComponent(searchCriteria.updatedFrom);
        }
        if(searchCriteria.updatedTo){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'toLastModifiedDate=' + encodeURIComponent(searchCriteria.updatedTo);
        }
        if(searchCriteria.type){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'type=' + encodeURIComponent(searchCriteria.type);
        }
        if(searchCriteria.status){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'status=' + encodeURIComponent(searchCriteria.status);
        }
        if(searchCriteria.contact){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'contact=' + encodeURIComponent(searchCriteria.contact);
        }
        if(searchCriteria.contactEmail){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'contactmail=' + encodeURIComponent(searchCriteria.contactEmail);
        }
        if(searchCriteria.category){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'category=' + encodeURIComponent(searchCriteria.category);
        }
        if(searchCriteria.priority){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'priority=' + encodeURIComponent(searchCriteria.priority);
        }
        if(searchCriteria.subject){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'subject=' + encodeURIComponent(searchCriteria.subject);
        }
        if(searchCriteria.description){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'description=' + encodeURIComponent(searchCriteria.description);
        }
        if(searchCriteria.assetId){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'assetId=' + encodeURIComponent(searchCriteria.assetId);
        }
        if(searchCriteria.asset){
            if(!query.endsWith('?')){
                query += '&';
            }
            // remove possibly blank serialNumber/name from search values
            let value = searchCriteria.asset.split('||').filter(s => (s && s != "" && s != "n/a")).join('||')
            query += 'asset=' + encodeURIComponent(value);
        }
        if(searchCriteria.escalation){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'escalation=' + encodeURIComponent(searchCriteria.escalation);
        }
        if(searchCriteria.sort){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'sort=' + encodeURIComponent(searchCriteria.sort);
        }
        if(searchCriteria.sortBy){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'sortBy=' + encodeURIComponent(searchCriteria.sortBy);
        }
        
        if(searchCriteria.limit){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'limit=' + encodeURIComponent(searchCriteria.limit);
        }
        
        if(searchCriteria.skip){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'offset=' + encodeURIComponent(searchCriteria.skip);
        }
        if (searchCriteria.securityAnaltyics){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'SecurityAnalytics=true';
        }

        return this.http
            .get(query)
            .toPromise()
            .then(response => response as Case[])
            .then(res => {
               var returnObj:any = {}
                if(searchCriteria.requestTracker.requestTracker){
                    returnObj = {
                        requestTracker:searchCriteria.requestTracker.count,
                        data: res
                    }
                }
                else {
                    returnObj = res;
                }
              
                return returnObj
            })
            .catch(this.handleError);
    }

    getLivePDFReport(searchCriteria: SupportCaseSearchCriteria){
     
        var headers = new HttpHeaders();
        headers = headers.set('Content-Encoding', 'gzip');
        headers = headers.set('Content-Type', 'application/pdf');
        headers = headers.set('Accept', 'application/pdf');

        let query = this.caseSearchURL + '?';
            
        query += 'downloadPDF=true';
        
        if(searchCriteria.createdFrom){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'fromCreatedDate=' + encodeURIComponent(searchCriteria.createdFrom);
        }
        if(searchCriteria.createdTo){
            if(!query.endsWith('?')){
                query += '&';
            }
            query += 'toCreatedDate=' + encodeURIComponent(searchCriteria.createdTo);
        }

        return this.http.get(query,
                              {headers: headers, responseType: 'blob'})
      .toPromise()
      .then(response => response as any)
      .catch(this.handleError);
    }



    private handleError(error: any): Promise<any> {
        if (error.status == 521){
            error.message = "Something is wrong with Account Mapping!"
        }
        console.error('An error occurred', error);
        return Promise.reject(error.message || error);
    }

}